import {CardBox} from '@hconnect/uikit/src/lib2'
import {Alert, Stack} from '@mui/material'
import {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {SignalsModalData} from '../../types'

import {AxialBalanceModal} from './AxialBalanceModal'
import {CrankSignalsModal} from './CrankSignalsModal'
import {HealthIndicators} from './HealthIndicators'
import {HealthIndicatorSignalsModal, HealthIndicatorsModalData} from './HealthIndicatorSignalsModal'
import {KilnConfigSettingsSteps} from './KilnConfigSettingsSteps'
import {KilnLayout} from './KilnLayout'
import {AddStationButton} from './KilnStationConfig'
import {
  CancelKilnChangesButton,
  OverallKilnConfigInputs,
  SaveKilnChangesTrigger
} from './OverallKilnConfig'
import {KilnRotationToggleButton} from './OverallKilnConfig/KilnRotationToggleButton'
import {KilnShellSignalsButton} from './OverallKilnConfig/KilnShellSignalsButton'
import {SignalsDisconnectWarning} from './SignalsDisconnectWarning'
import {SignalsModal} from './SignalsModal'

interface KilnConfigContentProps {
  kilnId: string
  isConfigMissing: boolean
  disabledSignals: string[]
  bypassNavigationBlocker: () => void
}

export const KilnConfigContent: React.FC<KilnConfigContentProps> = ({
  kilnId,
  isConfigMissing,
  disabledSignals,
  bypassNavigationBlocker
}) => {
  const {t} = useTranslation()

  const [signalsModalData, setSignalsModalData] = useState<SignalsModalData | undefined>()

  const [indicatorsModalData, setIndicatorsModalData] = useState<
    HealthIndicatorsModalData | undefined
  >()
  const [crankSignalsOpen, setCrankSignalsOpen] = useState(false)
  const [axialBalanceOpen, setAxialBalanceOpen] = useState(false)

  const [signalsDisconnectWarning, setSignalsDisconnectWarning] = useState<{
    open: boolean
    signals: string[]
    action: (() => void) | null
  }>({
    open: false,
    signals: [],
    action: null
  })

  return (
    <Stack spacing={2} maxWidth="fit-content" minWidth="fit-content">
      <CardBox display="flex" flexDirection="column" gap={4} data-test-id="kiln-config-card">
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <OverallKilnConfigInputs />
            <AddStationButton />
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <CancelKilnChangesButton bypassNavigationBlocker={bypassNavigationBlocker} />
            <SaveKilnChangesTrigger
              isConfigMissing={isConfigMissing}
              bypassNavigationBlocker={bypassNavigationBlocker}
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Stack spacing={2}>
            <Stack direction="row" spacing={3}>
              <KilnRotationToggleButton setSignalsDisconnectWarning={setSignalsDisconnectWarning} />
              <KilnShellSignalsButton
                disabledSignals={disabledSignals}
                setSignalsModalData={setSignalsModalData}
              />
              <HealthIndicators
                setCrankSignalsOpen={setCrankSignalsOpen}
                setAxialBalanceOpen={setAxialBalanceOpen}
                setIndicatorsModalData={setIndicatorsModalData}
              />
            </Stack>
            <Alert severity="warning" sx={{maxWidth: 'fit-content'}}>
              {t('kilnConfig.setupWarning')}
            </Alert>
            <KilnLayout
              setSignalsDisconnectWarning={setSignalsDisconnectWarning}
              setSignalsModalData={setSignalsModalData}
              disabledSignals={disabledSignals}
            />
          </Stack>
          <KilnConfigSettingsSteps />
        </Stack>
      </CardBox>
      {signalsModalData && (
        <SignalsModal
          kilnId={kilnId}
          handleClose={() => setSignalsModalData(undefined)}
          signalsModalData={signalsModalData}
        />
      )}
      {indicatorsModalData && (
        <HealthIndicatorSignalsModal
          handleClose={() => setIndicatorsModalData(undefined)}
          healthIndicatorsModalData={indicatorsModalData}
        />
      )}
      <SignalsDisconnectWarning
        open={signalsDisconnectWarning.open}
        onClose={() =>
          setSignalsDisconnectWarning({
            open: false,
            signals: [],
            action: null
          })
        }
        onConfirm={() => {
          signalsDisconnectWarning.action && signalsDisconnectWarning?.action()
          setSignalsDisconnectWarning({
            open: false,
            signals: [],
            action: null
          })
        }}
        signals={signalsDisconnectWarning.signals}
      />
      {crankSignalsOpen && <CrankSignalsModal handleClose={() => setCrankSignalsOpen(false)} />}
      {axialBalanceOpen && <AxialBalanceModal handleClose={() => setAxialBalanceOpen(false)} />}
    </Stack>
  )
}
